import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import { reportMonthLabel, type ClientReport, type ReportBlock } from '../../lib/refonteApi';

const CHART_COLORS = ['#e11d69', '#1f2937', '#6b7280', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f43f5e'];

// CSS d'impression : en « Enregistrer en PDF » (window.print), on masque le shell
// et on ne garde que le rapport. Injecté une fois.
const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  .report-print, .report-print * { visibility: visible; }
  .report-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
  .no-print { display: none !important; }
}`;

const isEmpty = (b: ReportBlock) => {
  if (b.type === 'text') return !b.body.trim();
  if (b.type === 'bullets') return b.items.filter(Boolean).length === 0;
  if (b.type === 'table') return b.rows.length === 0;
  if (b.type === 'chart') return !b.data.some(d => d.value);
  return false;
};

const Block: React.FC<{ b: ReportBlock }> = ({ b }) => {
  if (b.type === 'kpis') {
    // Grille adaptée au nombre de KPIs : jusqu'à 6 par ligne pour éviter le retour à la
    // ligne disgracieux (5+1) et le blanc. Tuiles compactes.
    const n = b.items.length;
    const lg = n <= 4 ? 'lg:grid-cols-4' : n === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-6';
    return (
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${lg} gap-2.5`}>
        {b.items.map((it, i) => (
          <div key={i} className="bg-panel border border-line rounded-card px-3 py-2.5">
            <div className="font-mono text-[9px] uppercase tracking-widest text-faint leading-tight">{it.label}</div>
            <div className="mt-1 text-[22px] font-bold font-mono leading-none tabular-nums">{it.value}</div>
            {it.hint && <div className="mt-1 text-[10.5px] text-dim leading-tight">{it.hint}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (b.type === 'table') {
    return (
      <div className="bg-panel border border-line rounded-card p-5">
        <h3 className="text-[14px] font-bold mb-3">{b.title}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-line-2">
                {b.columns.map((c, i) => <th key={i} className="text-left font-mono text-[9px] uppercase tracking-widest text-faint pb-2 pr-3">{c}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {b.rows.map((row, i) => (
                <tr key={i}>{row.map((cell, j) => <td key={j} className="py-2 pr-3 align-top">{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
        {b.note && <p className="font-mono text-[10px] text-faint mt-2">{b.note}</p>}
      </div>
    );
  }
  if (b.type === 'bullets') {
    return (
      <div className="bg-panel border border-line rounded-card p-5">
        <h3 className="text-[14px] font-bold mb-2">{b.title}</h3>
        <ul className="list-disc pl-5 space-y-1 text-[13px] text-dim">{b.items.filter(Boolean).map((it, i) => <li key={i}>{it}</li>)}</ul>
      </div>
    );
  }
  if (b.type === 'chart') {
    if (b.chart === 'pie') {
      const total = b.data.reduce((s, d) => s + d.value, 0) || 1;
      const sorted = b.data.map((d, i) => ({ ...d, color: CHART_COLORS[i % CHART_COLORS.length] })).sort((a, z) => z.value - a.value);
      return (
        <div className="bg-panel border border-line rounded-card p-5">
          <h3 className="text-[14px] font-bold mb-3">{b.title}</h3>
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="relative shrink-0" style={{ width: 190, height: 190 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={b.data} dataKey="value" nameKey="label" innerRadius={58} outerRadius={90} paddingAngle={2} stroke="none">
                    {b.data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`${v} (${Math.round((v / total) * 100)}%)`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[22px] font-bold font-mono leading-none">{total}</span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-faint">total</span>
              </div>
            </div>
            <ul className="flex-1 w-full space-y-1.5">
              {sorted.map(d => (
                <li key={d.label} className="flex items-center gap-2 text-[12.5px]">
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: d.color }} />
                  <span className="flex-1 min-w-0 truncate">{d.label}</span>
                  <span className="font-mono font-semibold">{d.value}</span>
                  <span className="font-mono text-faint w-11 text-right">{Math.round((d.value / total) * 100)}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    return (
      <div className="bg-panel border border-line rounded-card p-5">
        <h3 className="text-[14px] font-bold mb-3">{b.title}</h3>
        <div style={{ width: '100%', height: 270 }}>
          <ResponsiveContainer>
            <BarChart data={b.data} margin={{ top: 22, right: 8, bottom: 4, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={10} allowDecimals={false} width={30} tickLine={false} axisLine={false} />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="value" fill="#e11d69" radius={[6, 6, 0, 0]} maxBarSize={64}>
                <LabelList dataKey="value" position="top" fontSize={11} fontWeight={600} fill="#1f2937" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
  // text
  return (
    <div className="bg-panel border border-line rounded-card p-5">
      <h3 className="text-[14px] font-bold mb-2">{b.title}</h3>
      <p className="text-[13px] text-dim leading-relaxed whitespace-pre-wrap">{b.body}</p>
    </div>
  );
};

/** Rend une liste de blocs (réutilisé par l'analyse live client). */
export const ReportBlocks: React.FC<{ blocks: ReportBlock[] }> = ({ blocks }) => (
  <div className="space-y-4">
    {blocks.filter(b => !b.hidden && !isEmpty(b)).map(b => <Block key={b.id} b={b} />)}
  </div>
);

/** Rend UN bloc (ou rien si vide/absent) — pour composer une mise en page par sections. */
export const Blk: React.FC<{ block?: ReportBlock }> = ({ block }) => (block && !block.hidden && !isEmpty(block) ? <Block b={block} /> : null);

/** Rendu lecture seule d'un rapport (blocs typés). Utilisé côté client (publié) et
 *  en aperçu côté CSM. Les blocs vides ou masqués sont ignorés. */
const ReportView: React.FC<{ report: ClientReport }> = ({ report }) => {
  const blocks = report.sections.filter(b => !b.hidden && !isEmpty(b));
  return (
    <div className="report-print space-y-4">
      <style>{PRINT_CSS}</style>
      <div>
        <h2 className="text-[19px] font-bold">{report.title || `Rapport — ${reportMonthLabel(report.period_year, report.period_month)}`}</h2>
        <p className="font-mono text-[10px] text-faint">Hooklab · {reportMonthLabel(report.period_year, report.period_month)}</p>
      </div>
      {blocks.map(b => <Block key={b.id} b={b} />)}
      <p className="font-mono text-[10px] text-faint">Document généré à partir des données Hooklab (Supabase) — confidentiel.</p>
    </div>
  );
};

export default ReportView;
